import { useNavigate } from "@solidjs/router";
import { createSignal } from "solid-js";

import { useI18n } from "@uic/adapters/i18n";
import { UIButton, UIInput } from "@uic/domain/generics";

import { useAuthn } from "../contexts/authn-context";

import { FloatingCard } from "./floating-card";

/**
 * @returns {import('solid-js').JSX.Element} The Signup component
 */
export function Signup() {
  const [email, setEmail] = createSignal("");
  const [name, setName] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal("");

  const nav = useNavigate();
  const { passwordSignup } = useAuthn();
  const { t } = useI18n();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email() || !name() || !password()) {
      setError(t("generics.validation.allFieldsRequired"));
      return;
    }

    try {
      await passwordSignup(email(), name(), password());
      nav("/onboarding");
    } catch (err) {
      if (err?.response?.status === 403) {
        setError(t("domain.authn.signupNotAllowed"));
      } else {
        setError(err.message || t("domain.authn.signupFailed"));
      }
    }
  };

  return (
    <FloatingCard>
      <h1 className="tw:mx-auto tw:mb-6 tw:font-serif tw:text-wordmark-lg tw:font-bold">
        {t("domain.authn.join", { statement: t("slorg.statement") })}
      </h1>

      <form onSubmit={handleSubmit}>
        <fieldset className="tw:mx-auto tw:d-fieldset tw:flex tw:w-xs tw:flex-col tw:gap-4 tw:text-dense ">
          <legend className="tw:d-fieldset-legend tw:text-section">
            {t("domain.authn.signup")}
          </legend>
          <UIInput
            type="email"
            label={t("generics.inputLabels.email")}
            value={email()}
            onInput={(e) => setEmail(e.target.value)}
            placeholder={t("generics.inputLabels.email")}
            required
          />

          <UIInput
            label={t("generics.inputLabels.name")}
            value={name()}
            onInput={(e) => setName(e.target.value)}
            placeholder={t("generics.inputLabels.name")}
            required
          />

          <UIInput
            type="password"
            label={t("generics.inputLabels.password")}
            placeholder={t("generics.inputLabels.password")}
            value={password()}
            onInput={(e) => setPassword(e.target.value)}
            error={error()}
            required
          />

          <UIButton type="submit" variant="accent">
            {t("domain.authn.signup")}
          </UIButton>
        </fieldset>
      </form>
    </FloatingCard>
  );
}
